// src/pages/gene.codes/GeneticWheel/WheelModal.tsx
import React, { useState, useEffect } from "react";
import { Button, Layout as AntLayout, Segmented } from "antd";
import { BarChartOutlined, CloseOutlined, UnorderedListOutlined } from "@ant-design/icons";

import InterventionsDisplay from "./wheels/InterventionsDisplay";
import WellnessSynopsis from "./wheels/WellnessSynopsis";

// Lazy: a static import pulls AncestryMap's CSS and its world-atlas fetch path into the main chunk.
const AncestryWorkspace = React.lazy(() => import("./Ancestry/AncestryWorkspace"));
import { InterventionData } from "./wheels/InterventionsDisplay";
import ZoomableSunburst from "./wheels/ZoomableSunburst";
import Sunburst from "./wheels/Sunburst";
import InterventionIconRow from "./wheels/InterventionIconRow";
import InterventionEvidencePanel from "./wheels/InterventionEvidencePanel";
import DetailPanel from "./dashboardPanels/DetailPanel";
import { DETAIL_MIN_WIDTH, MIN_WHEEL, detailWidthFor, wheelSizeFor } from "../../../utils/geneCodes/wheelLayout";
import { WheelSelection, depthOf, selectGene, selectSnp, selectTrait } from "../../../utils/geneCodes/wheelSelection";
import InterventionsSidebar from "./dashboardPanels/InterventionsSidebar";
import PanelList from "./dashboardPanels/PanelList";

import { Patient } from "../../../types/healthcare/PatientTypes";
import { Sample } from "../../../types/lab/SampleTypes";
import { Trait } from "../../../types/geneCodes/TraitTypes";
import { Staging } from "../../../types/geneCodes/StagingTypes";

import { useNotifications } from "../../../context/NotificationContext";
import { fetchInterventionsData } from "../../../api/geneCodes/InterventionsApi";
import { useGeneticData } from "../../../hooks/genetic/useGeneticData";
import { useSnpInterventions } from "../../../hooks/genetic/useSnpInterventions";
import { usePreference } from "../../../utils/userPrefs/UserPrefs";
import { DetailDrawer, InlineLoading } from "../../../components/shared";
import { MOTION } from "../../../design-system/tokens";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { resolveAncestryVisibility, AncestrySurface } from "./Ancestry/ancestryVisibility";
import AncestryViewTabs, { type AncestryView } from "./Ancestry/AncestryViewTabs";
import AiPanelGenerationControl from "./AiPanelGenerationControl";
import "./wheelShell.css";

import { staffCan } from "../../../utils/userPrefs/validatePermissions";
import { normaliseSex } from "../../../utils/geneCodes/referenceRange";

const { Sider, Content } = AntLayout;

interface GeneticSampleAnalysisModalProps {
	isOpen: boolean;
	onClose: () => void;
	patient?: Patient;
	sample: Sample;
	// Required for ancestry to be reachable at all — the visibility gate fails closed without it.
	surface?: AncestrySurface;
}

// SYNOPSIS_WIDTH is mirrored into --wheel-synopsis-w on the shell root, so CSS never re-types it.
const SYNOPSIS_WIDTH = 300;
const SYNOPSIS_COLUMN = SYNOPSIS_WIDTH + 16;
// 8px row padding + a ~30px pill (20px badge + 4px padding + 1px border, doubled), rounded up.
const ICON_ROW_HEIGHT = 40;
const DETAIL_COLUMN = DETAIL_MIN_WIDTH;
const RAIL_COLLAPSED_WIDTH = 32;
const MODAL_CHROME_HEIGHT = 100;
const STAGE_GUTTER = 32;
const COMPACT_GUTTER = 12;
// Toolbar + stage padding + icon row, so a phone never gets a wheel taller than its screen.
const COMPACT_CHROME_HEIGHT = 180;
// Below this the three-column model is replaced, not shrunk; above it the rail floors closed.
const COMPACT_BREAKPOINT = 768;
const RAIL_FLOOR_BREAKPOINT = 992;
const VIEW_TRANSITION_MS = Number.parseInt(MOTION.duration.normal, 10);

type ViewMode = "interventions" | "panel" | "ancestry";
type WheelInk = "dark" | "light";

const INK_OPTIONS = [
	{ label: "Dark ink", value: "dark" },
	{ label: "Light ink", value: "light" },
];

const railWidthFor = (viewportWidth: number) => (viewportWidth < COMPACT_BREAKPOINT ? 260 : viewportWidth < RAIL_FLOOR_BREAKPOINT ? 280 : 300);

const wheelLayoutFor = (viewportWidth: number, viewportHeight: number, railInFlow: boolean, mode: ViewMode) => {
	if (viewportWidth < COMPACT_BREAKPOINT) {
		const room = Math.min(viewportWidth - COMPACT_GUTTER * 2, viewportHeight - COMPACT_CHROME_HEIGHT);
		return { size: Math.max(MIN_WHEEL, room), stacked: true, detailWidth: DETAIL_COLUMN };
	}

	// ⚠️ THE REAL VIEWPORT, BOTH AXES. This used to clamp to a 1400x800 modal that no longer
	// exists — the shell is a DetailDrawer at width="100%", full viewport height — and the clamp
	// only starved the wheel: on a 1878x918 monitor it capped the circle at 660 with 118px of
	// height and 478px of width still unused.
	const railWidth = railInFlow ? railWidthFor(viewportWidth) : RAIL_COLLAPSED_WIDTH;
	const centreWidth = viewportWidth - railWidth - DETAIL_COLUMN - STAGE_GUTTER;
	const belowWheel = mode === "panel" ? ICON_ROW_HEIGHT : 0;
	const availableHeight = viewportHeight - MODAL_CHROME_HEIGHT - belowWheel;

	// The wheel stays square; the synopsis column comes off the width prop at render, never off size.
	const size = wheelSizeFor(centreWidth, availableHeight);

	return { size, stacked: centreWidth < SYNOPSIS_COLUMN + MIN_WHEEL, detailWidth: detailWidthFor(centreWidth, size) };
};

const SHELL_VARS = { "--wheel-synopsis-w": `${SYNOPSIS_WIDTH}px` } as React.CSSProperties;

const GeneticSampleAnalysisModal: React.FC<GeneticSampleAnalysisModalProps> = ({ isOpen, onClose, patient, sample, surface }) => {
	const [viewMode, setViewMode] = useState<ViewMode>("interventions");
	const [wheelInk, setWheelInk] = useState<WheelInk>("dark");
	const [isTransitioning, setIsTransitioning] = useState(false);

	const [interventionsData, setInterventionsData] = useState<any>({});
	const [selectedIntervention, setSelectedIntervention] = useState<InterventionData | null>(null);

	const [selectedInterventions, setSelectedInterventions] = useState<string[]>([]);
	const [evidenceCode, setEvidenceCode] = useState<string | null>(null);

	const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
	const [ancestryView, setAncestryView] = useState<AncestryView>("ancestry");
	const [selectedTrait, setSelectedTrait] = useState<Trait | null>(null);
	const [selectedGene, setSelectedGene] = useState<string | null>(null);
	const [selectedSnp, setSelectedSnp] = useState<Staging | null>(null);
	const [activeIndex, setActiveIndex] = useState<number>(1);

	// `true` = zoomable (one ring at a time), `false` = the multi-ring sunburst.
	// Mobile defaults to zoomable because the toggle lives in headerActions, which is hidden
	// below COMPACT_BREAKPOINT — on a phone the default is the only mode the user gets.
	const [wheelMode, setWheelMode] = useState<boolean>(() => window.innerWidth < COMPACT_BREAKPOINT);
	const [windowSize, setWindowSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const [storedRailCollapsed, setStoredRailCollapsed] = usePreference("geneWheelRailCollapsed");
	const [railCollapsed, setRailCollapsed] = useState<boolean>(storedRailCollapsed);
	const [narrowRailOpen, setNarrowRailOpen] = useState(false);

	const currentUser = getSessionUser();

	// Fails closed: no surface prop, an unrecognised one, or a patient viewer all resolve to hidden.
	const ancestryVisibility = resolveAncestryVisibility({ surface, userType: currentUser.user_type });
	const ancestryAvailable = ancestryVisibility.reachable;

	// Generation is desktop-only: mobile/compact mode must not expose a trigger.
	const mayGenerateAiPanels = staffCan("labiq.genecodes.generate");

	const compact = windowSize.width < COMPACT_BREAKPOINT;
	const narrowViewport = windowSize.width < RAIL_FLOOR_BREAKPOINT;
	// A narrow viewport is a FLOOR on the rail, not an override — the stored preference is restored, never rewritten, above 992.
	const leftCollapsed = narrowViewport ? !narrowRailOpen : railCollapsed;
	// Only a rail that is genuinely in the flow costs the stage any width.
	const railInFlow = !leftCollapsed && !narrowViewport;
	const railOverlay = narrowViewport && !leftCollapsed && !compact;

	const { showInfo, showError, showSuccess, showWarning } = useNotifications();
	// Most accounts hold sex only in `gender`, or nothing at all, so both are read.
	const railSex = normaliseSex(patient?.sex, patient?.gender);

	const { panels, selectedPanel, traits, snps, geneticData, loading, error, selectPanel, aiPanelData } = useGeneticData({
		sampleId: sample.sampleID,
		scopeToSample: true,
		sex: railSex,
	});

	// labiq drops a sex-specific wheel for any M/F it is sent, so an unknown sex sends none rather than defaulting to F.
	const interventionSex = railSex === "male" ? "M" : railSex === "female" ? "F" : undefined;

	// An undefined category leaves the query disabled, so no request fires outside panel view.
	const { modifierMap, interventions, isLoading: interventionsLoading } = useSnpInterventions(sample.sampleID, viewMode === "panel" ? selectedPanel?.name_panel : undefined, interventionSex);

	const { size: wheelSize, stacked, detailWidth } = wheelLayoutFor(windowSize.width, windowSize.height, railInFlow, viewMode);

	useEffect(() => {
		const init = async () => {
			try {
				let data = await fetchInterventionsData(sample.sampleID, interventionSex);
				setInterventionsData(data.data);
			} catch (error) {
				showError("Failed to load interventions data");
			}
		};

		if (isOpen) {
			init();
			setViewMode("interventions");
			setSelectedIntervention(null);
			setSelectedTrait(null);
			setSelectedGene(null);
			setSelectedSnp(null);
			setActiveIndex(1);
			setSelectedInterventions([]);
			setEvidenceCode(null);
			setAncestryView("ancestry");
			// TestResultsTab keeps this modal mounted between opens, so the initialiser alone would go stale there.
			setWheelMode(window.innerWidth < COMPACT_BREAKPOINT);
		}
	}, [isOpen, patient, sample.sampleID, interventionSex]);

	useEffect(() => {
		setSelectedInterventions([]);
		setEvidenceCode(null);
	}, [selectedPanel?.id_panels]);

	useEffect(() => {
		const handleResize = () => {
			setWindowSize((prev) => (prev.width === window.innerWidth && prev.height === window.innerHeight ? prev : { width: window.innerWidth, height: window.innerHeight }));
		};

		window.addEventListener("resize", handleResize);
		handleResize();
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const transitionToViewMode = async (newMode: ViewMode) => {
		setIsTransitioning(true);
		await new Promise((resolve) => setTimeout(resolve, VIEW_TRANSITION_MS));
		setViewMode(newMode);
		setIsTransitioning(false);
	};

	const handlePanelFromIntervention = async (panelId: number) => {
		setSelectedTrait(null);
		setSelectedGene(null);
		setSelectedSnp(null);
		setActiveIndex(1);
		// The narrow rail covers the stage, so a selection made in it has to hand the stage back.
		setNarrowRailOpen(false);

		selectPanel(panelId, sample.sampleID);

		await transitionToViewMode("panel");
	};

	const handleBackToInterventions = async () => {
		setSelectedTrait(null);
		setSelectedGene(null);
		setSelectedSnp(null);
		setActiveIndex(1);
		setNarrowRailOpen(false);

		await transitionToViewMode("interventions");
	};

	const handleAncestry = async () => {
		setNarrowRailOpen(false);
		await transitionToViewMode("ancestry");
	};

	const toggleRail = () => {
		if (narrowViewport) {
			setNarrowRailOpen((prev) => !prev);
			return;
		}

		const next = !railCollapsed;
		setRailCollapsed(next);
		void setStoredRailCollapsed(next);
	};

	// The tabs are a breadcrumb, so every click resolves the WHOLE path, not just its own level.
	// DetailPanel keys its own tab off the selections themselves; `activeIndex` only mirrors them.
	const applySelection = (selection: WheelSelection) => {
		setSelectedTrait(selection.trait);
		setSelectedGene(selection.gene);
		setSelectedSnp(selection.snp);
		setActiveIndex(depthOf(selection));
	};

	const handleTraitClick = (trait: Trait) => applySelection(selectTrait(trait));

	// The rail hands over a trait NAME: staging's `subcategory` IS the trait name, the same join selectSnp makes.
	const handleTraitFromRail = (traitName: string) => {
		const trait = traits.find((entry) => entry.trait_name === traitName);
		if (trait) handleTraitClick(trait);
	};

	const handleGeneClick = (gene: string) => applySelection(selectGene(gene, snps, traits, selectedTrait));

	const handleSnpClick = (snp: Staging) => applySelection(selectSnp(snp, traits));

	const handleWheelModeChange = () => {
		setWheelMode((prev) => !prev);
	};

	const handleInterventionToggle = (code: string) => {
		setSelectedInterventions((prev) => (prev.includes(code) ? prev.filter((entry) => entry !== code) : [...prev, code]));
	};

	const handleInterventionClearAll = () => {
		setSelectedInterventions([]);
	};

	const evidenceDescription = interventions.find((entry) => entry.code === evidenceCode)?.description;

	const isAncestry = viewMode === "ancestry";

	const renderPanelSidebar = () => (
		<div className="wheel-shell__detail">
			<div className="wheel-shell__detail-scroll" style={{ overflowY: compact ? "visible" : "auto" }}>
				{loading.selectedPanel ? (
					<div className="wheel-shell__centred">
						<InlineLoading message="Loading panel data…" />
					</div>
				) : (
					<DetailPanel
						selectedPanel={selectedPanel}
						selectedTrait={selectedTrait}
						selectedGene={selectedGene}
						selectedSnp={selectedSnp}
						snps={snps}
						loading={loading.selectedPanel}
						activeIndex={activeIndex}
						setActiveIndex={setActiveIndex}
						aiPanelData={aiPanelData}
						geneticData={geneticData}
					/>
				)}
			</div>
		</div>
	);

	const renderMainContent = () => {
		if (isTransitioning) {
			return (
				<div className="wheel-shell__centred">
					<InlineLoading message="Transitioning…" />
				</div>
			);
		}

		if (viewMode === "ancestry") {
			return (
				<div className="wheel-shell__ancestry-body">
					<React.Suspense fallback={<InlineLoading message="Loading ancestry…" />}>
						<AncestryWorkspace sampleId={sample.sampleID} view={ancestryView} selectedCountry={selectedCountry} onCountrySelect={setSelectedCountry} />
					</React.Suspense>
				</div>
			);
		}

		if (viewMode === "interventions") {
			const wheelWidth = stacked ? wheelSize : wheelSize - SYNOPSIS_COLUMN;

			return (
				<div className={`wheel-shell__interventions${stacked ? " wheel-shell__interventions--stacked" : ""}`}>
					<div className="wheel-shell__interventions-main">
						<div className="wheel-shell__wheel">
							<InterventionsDisplay data={interventionsData} setSelectedIntervention={setSelectedIntervention} width={wheelWidth} height={wheelSize} />
						</div>
						<div className="wheel-shell__intervention-detail">
							<InterventionsSidebar selectedIntervention={selectedIntervention} isTransitioning={isTransitioning} />
						</div>
					</div>
					<div className="wheel-shell__synopsis">
						<WellnessSynopsis sampleId={sample.sampleID} />
					</div>
				</div>
			);
		}

		if (!selectedPanel) {
			return (
				<div className="wheel-shell__centred">
					<InlineLoading message="Loading panel…" />
				</div>
			);
		}

		if (loading.selectedPanel || loading.traits || loading.snps) {
			return (
				<div className="wheel-shell__centred">
					<InlineLoading message="Loading genetic data…" />
				</div>
			);
		}

		return (
			<div className="wheel-shell__panel-view">
				{wheelMode ? (
					<ZoomableSunburst
						selectedPanel={selectedPanel}
						traits={traits}
						snps={snps}
						geneticResults={geneticData}
						onTraitClick={handleTraitClick}
						onGeneClick={handleGeneClick}
						onSnpClick={handleSnpClick}
						width={wheelSize}
						height={wheelSize}
						ink={wheelInk}
						modifierMap={modifierMap}
						selectedInterventions={selectedInterventions}
						hideUngenotyped
					/>
				) : (
					<Sunburst
						selectedPanel={selectedPanel}
						traits={traits}
						snps={snps}
						geneticResults={geneticData}
						onTraitClick={handleTraitClick}
						onGeneClick={handleGeneClick}
						onSnpClick={handleSnpClick}
						width={wheelSize}
						height={wheelSize}
						ink={wheelInk}
						modifierMap={modifierMap}
						selectedInterventions={selectedInterventions}
						hideUngenotyped
					/>
				)}

				<InterventionIconRow
					interventions={interventions}
					selected={selectedInterventions}
					onToggle={handleInterventionToggle}
					onClearAll={handleInterventionClearAll}
					onShowEvidence={setEvidenceCode}
					loading={interventionsLoading}
				/>
			</div>
		);
	};

	const railRowKeyDown = (activate: () => void) => (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			activate();
		}
	};

	const railRowClass = (active: boolean) => `wheel-shell__rail-row${active ? " wheel-shell__rail-row--active" : ""}`;

	const renderPanelList = () => (
		<PanelList
			panels={panels}
			selectedPanelId={viewMode === "panel" ? selectedPanel?.id_panels ?? null : null}
			onSelectPanel={handlePanelFromIntervention}
			loading={loading.panels}
			sampleId={sample.sampleID}
			selectedTraitName={selectedTrait?.trait_name ?? null}
			onSelectTrait={handleTraitFromRail}
			hideUngenotyped
		/>
	);

	const renderRailNav = () => (
		<>
			<div
				role="button"
				tabIndex={0}
				data-testid="wheel-rail-interventions"
				className={railRowClass(viewMode === "interventions")}
				onClick={handleBackToInterventions}
				onKeyDown={railRowKeyDown(handleBackToInterventions)}
			>
				<BarChartOutlined />
				Actionable interventions
			</div>

			{ancestryAvailable && (
				<div role="button" tabIndex={0} data-testid="wheel-rail-ancestry" className={railRowClass(viewMode === "ancestry")} onClick={handleAncestry} onKeyDown={railRowKeyDown(handleAncestry)}>
					Ancestry
				</div>
			)}
		</>
	);

	const headerActions =
		viewMode === "panel" ? (
			<div className="wheel-shell-header-actions">
				<Button size="small" onClick={handleWheelModeChange} disabled={isTransitioning}>
					Change wheel mode
				</Button>
				<Segmented size="small" value={wheelInk} onChange={(value) => setWheelInk(value as WheelInk)} options={INK_OPTIONS} aria-label="Wheel ink" />
				{mayGenerateAiPanels && <AiPanelGenerationControl sampleId={sample.sampleID} disabled={isTransitioning} />}
			</div>
		) : viewMode === "ancestry" ? (
			<AncestryViewTabs view={ancestryView} onChange={setAncestryView} />
		) : undefined;

	const stage = (
		<Content className={`wheel-shell__content${isAncestry ? " wheel-shell__content--ancestry" : ""}`}>
			<div className={`wheel-shell__stage${isAncestry ? " wheel-shell__stage--ancestry" : ""}`}>{renderMainContent()}</div>
		</Content>
	);

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			width="100%"
			title={`${patient?.first_name ?? ""} ${patient?.last_name ?? ""} — Sample ${sample.sampleID}`}
			subtitle={viewMode === "interventions" ? "Genetic interventions analysis" : isAncestry ? "Ancestry composition" : `Panel analysis: ${selectedPanel?.name_panel || "Loading…"}`}
			headerActions={windowSize.width >= COMPACT_BREAKPOINT ? headerActions : undefined}
			noPadding
		>
			{/* Mounted for the life of the modal: the drawer's query observer owns the stale-window de-dupe. */}
			<InterventionEvidencePanel
				open={!!evidenceCode}
				code={evidenceCode}
				description={evidenceDescription}
				sampleId={sample.sampleID}
				panelName={selectedPanel?.name_panel || ""}
				onClose={() => setEvidenceCode(null)}
			/>

			<AntLayout className={`wheel-shell${compact ? " wheel-shell--compact" : ""}${isTransitioning ? " wheel-shell--transitioning" : ""}`} style={SHELL_VARS}>
				{compact ? (
					<>
						<div className="wheel-shell__compact-bar">
							<Button size="small" icon={<UnorderedListOutlined />} onClick={toggleRail} aria-label={narrowRailOpen ? "Hide panels" : "Show panels"} data-testid="wheel-rail-toggle">
								Panels
							</Button>
							<Button
								size="small"
								type={viewMode === "interventions" ? "primary" : "default"}
								icon={<BarChartOutlined />}
								onClick={handleBackToInterventions}
								aria-label="Actionable interventions"
								data-testid="wheel-rail-interventions"
							/>
							{ancestryAvailable && (
								<Button size="small" type={isAncestry ? "primary" : "default"} onClick={handleAncestry} data-testid="wheel-rail-ancestry">
									Ancestry
								</Button>
							)}
						</div>

						<div className="wheel-shell__compact-scroll">
							{stage}
							{viewMode === "panel" && <section className="wheel-shell__detail-stacked">{renderPanelSidebar()}</section>}
						</div>

						{narrowRailOpen && (
							<>
								<div className="wheel-scrim" aria-hidden="true" onClick={toggleRail} />
								<div className="wheel-shell__rail-overlay" data-testid="wheel-rail">
									<div className="wheel-rail-head">
										Panels
										<Button type="text" size="small" icon={<CloseOutlined />} onClick={toggleRail} aria-label="Hide panels" />
									</div>
									<div className="wheel-shell__rail-panels">{renderPanelList()}</div>
								</div>
							</>
						)}
					</>
				) : (
					<>
						{railOverlay && <div className="wheel-scrim" aria-hidden="true" onClick={toggleRail} />}

						<Sider
							collapsed={leftCollapsed}
							collapsedWidth={RAIL_COLLAPSED_WIDTH}
							width={railWidthFor(windowSize.width)}
							className={`wheel-shell__rail${railOverlay ? " wheel-shell__rail--overlay" : ""}`}
						>
							<Button
								type="text"
								icon={leftCollapsed ? "›" : "‹"}
								onClick={toggleRail}
								aria-label={leftCollapsed ? "Show panels" : "Hide panels"}
								data-testid="wheel-rail-toggle"
								className={`wheel-shell__rail-toggle${leftCollapsed ? " wheel-shell__rail-toggle--collapsed" : ""}`}
							/>

							{leftCollapsed ? (
								// The rail row is the only way back to interventions, so it survives the collapse as an icon.
								<div className="wheel-shell__rail-collapsed">
									<Button type="text" size="small" icon={<BarChartOutlined />} title="Actionable interventions" aria-label="Actionable interventions" data-testid="wheel-rail-interventions" onClick={handleBackToInterventions} />
								</div>
							) : (
								<div className="wheel-shell__rail-body" data-testid="wheel-rail">
									{renderRailNav()}
									<div className="wheel-shell__rail-panels">{renderPanelList()}</div>
								</div>
							)}
						</Sider>

						{stage}

						{viewMode === "panel" && (
							<Sider collapsed={false} collapsedWidth={RAIL_COLLAPSED_WIDTH} width={detailWidth} className="wheel-shell__detail-sider">
								{renderPanelSidebar()}
							</Sider>
						)}
					</>
				)}
			</AntLayout>
		</DetailDrawer>
	);
};

export default GeneticSampleAnalysisModal;
